import * as _ from 'lodash'
import moment from 'moment'
import { US_DATE_FORMAT } from '../../utils/format'
import { AnswerPayload, isTextBlockQuestionType, Question, QuestionSetResponse, QuestionType } from '../types'

type QuestionDefaultAnswer = string | boolean | unknown[] | Record<string, unknown> | null

export const questionTypeCodeToDefaultAnswer: Record<QuestionType, QuestionDefaultAnswer> = {
  [QuestionType.SHORT_TEXT]: '',
  [QuestionType.LONG_TEXT]: '',
  [QuestionType.SINGLE_CHOICE]: '',
  [QuestionType.DROPDOWN]: '',
  [QuestionType.MULTIPLE_CHOICE]: [],
  [QuestionType.TRUE_OR_FALSE]: false,
  [QuestionType.NUMBER]: '',
  [QuestionType.PHONE_NUMBER]: '',
  [QuestionType.EMAIL]: '',
  [QuestionType.DOCUMENT]: '',
  [QuestionType.HOME_ADDRESS]: {},
  [QuestionType.DATE_OF_BIRTH]: null,
  [QuestionType.EMERGENCY_CONTACT]: {},
  [QuestionType.TEXT_BLOCK]: '',
}

export interface ResponseByQuestionId {
  [key: string]: any
}

const normalizeDateOfBirthResponse = (response: unknown) => {
  if (response == null) {
    return null
  }
  if (typeof response === 'string') {
    const trimmed = response.trim()
    if (trimmed === '') {
      return null
    }
    const parsed = moment(trimmed, US_DATE_FORMAT, true)
    return parsed.isValid() ? parsed.toDate() : trimmed
  }
  return response
}

const serializeDateOfBirthResponse = (response: unknown) => {
  if (response == null) {
    return null
  }
  if (moment.isMoment(response)) {
    return response.format(US_DATE_FORMAT)
  }
  if (response instanceof Date) {
    return moment(response).format(US_DATE_FORMAT)
  }
  if (typeof response === 'string') {
    const trimmed = response.trim()
    return trimmed === '' ? null : trimmed
  }
  return response
}

const isDateOfBirthCompleted = (response: unknown) => {
  if (response == null) {
    return false
  }
  if (moment.isMoment(response)) {
    return response.isValid()
  }
  if (response instanceof Date) {
    return moment(response).isValid()
  }
  if (typeof response === 'string') {
    const trimmed = response.trim()
    return trimmed !== '' && moment(trimmed, US_DATE_FORMAT, true).isValid()
  }
  return false
}

const getResponse = (question: Question) => {
  if (question.questionType === QuestionType.DATE_OF_BIRTH) {
    return normalizeDateOfBirthResponse(question.response)
  }
  return _.isEmpty(question.response) ? questionTypeCodeToDefaultAnswer[question.questionType] : question.response
}

export const initQuestionAnswers = (questionSetResponse: QuestionSetResponse): Question[] => {
  if (!questionSetResponse) {
    return []
  }
  const { questions } = questionSetResponse

  const responseByQuestionId: ResponseByQuestionId = questions.reduce(
    (acc: ResponseByQuestionId, question: Question) => {
      acc[question.questionId] = getResponse(question)
      return acc
    },
    {},
  )
  return questions.map(question => {
    const { conditionedOnQuestionId, conditionedOnAnswer, otherAnswer } = question
    return {
      ...question,
      response: getResponse(question),
      otherAnswer: _.isEmpty(otherAnswer) ? '' : otherAnswer,
      disabled: conditionedOnQuestionId ? responseByQuestionId[conditionedOnQuestionId] !== conditionedOnAnswer : false,
    }
  })
}

export const mapQuestionToAnswerPayload = (questionWithAnswer: Question): AnswerPayload => ({
  answerSetId: _.get(questionWithAnswer, 'answerSet.id', undefined),
  answerId: _.get(questionWithAnswer, 'answerId', undefined),
  questionId: questionWithAnswer.questionId,
  response: questionWithAnswer.disabled
    ? questionTypeCodeToDefaultAnswer[questionWithAnswer.questionType]
    : questionWithAnswer.questionType === QuestionType.DATE_OF_BIRTH
    ? serializeDateOfBirthResponse(questionWithAnswer.response)
    : questionWithAnswer.response,
  otherAnswer: questionWithAnswer.disabled ? '' : questionWithAnswer.otherAnswer,
  completed: isTextBlockQuestionType(questionWithAnswer.questionType)
    ? true
    : questionWithAnswer.disabled
    ? false
    : questionWithAnswer.questionType === QuestionType.DATE_OF_BIRTH
    ? isDateOfBirthCompleted(questionWithAnswer.response)
    : !_.isEmpty(questionWithAnswer.response),
})

export const OTHER_OPTION = 'Other'
